import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Building2, 
  Calendar, 
  Users, 
  Heart, 
  Smile, 
  Eye, 
  DollarSign,
  AlertCircle,
  FileText,
  ChevronDown,
  ChevronRight,
  Filter,
  X,
  Check,
  SlidersHorizontal,
  Shield,
  TrendingUp,
  Percent,
  Download,
  Share2,
  ArrowUpDown,
  Grid3X3,

  UserCheck,
  Phone,
  Mail,
  BadgeCheck,
  User,
  Baby,
  Info,
} from "lucide-react";
import { cn, formatDateLong } from "@/lib/utils";
import { computePlanCostRange, formatMonthlyRange } from "@/lib/quoteCostRange";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { QuoteAcceptanceForm } from "@/components/quoting/QuoteAcceptanceForm";

type ProposalTemplate = "modern" | "classic" | "minimal" | "corporate";
type ColorScheme = "blue" | "green" | "purple" | "orange" | "slate";

interface ProposalSettings {
  template: ProposalTemplate;
  colorScheme: ColorScheme;
  showLogo: boolean;
  showBenefitsDetail: boolean;
  showEmployeeBreakdown: boolean;
  coverLetter: string;
}

const colorSchemeClasses: Record<ColorScheme, { primary: string; accent: string; text: string; bg: string; border: string }> = {
  blue: { primary: "bg-blue-600", accent: "bg-blue-50 dark:bg-blue-950", text: "text-blue-600 dark:text-blue-400", bg: "from-blue-600 to-indigo-700", border: "border-blue-200 dark:border-blue-800" },
  green: { primary: "bg-emerald-600", accent: "bg-emerald-50 dark:bg-emerald-950", text: "text-emerald-600 dark:text-emerald-400", bg: "from-emerald-600 to-teal-700", border: "border-emerald-200 dark:border-emerald-800" },
  purple: { primary: "bg-purple-600", accent: "bg-purple-50 dark:bg-purple-950", text: "text-purple-600 dark:text-purple-400", bg: "from-purple-600 to-violet-700", border: "border-purple-200 dark:border-purple-800" },
  orange: { primary: "bg-orange-600", accent: "bg-orange-50 dark:bg-orange-950", text: "text-orange-600 dark:text-orange-400", bg: "from-orange-600 to-red-600", border: "border-orange-200 dark:border-orange-800" },
  slate: { primary: "bg-slate-600", accent: "bg-slate-50 dark:bg-slate-950", text: "text-slate-600 dark:text-slate-400", bg: "from-slate-600 to-slate-800", border: "border-slate-200 dark:border-slate-700" },
};

const formatCurrency = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) return "$0.00";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(num);
};

const getCoverageIcon = (type: string) => {
  switch (type?.toLowerCase()) {
    case "health":
    case "medical":
      return Heart;
    case "dental":
      return Smile;
    case "vision":
      return Eye;
    default:
      return Heart;
  }
};

const metalTierOrder: Record<string, number> = {
  'Platinum': 1,
  'Gold': 2,
  'Silver': 3,
  'Bronze': 4,
  'Minimum Coverage': 5,
  'Catastrophic': 6,
};

const metalTierColors: Record<string, string> = {
  'Platinum': 'bg-gradient-to-r from-slate-300 to-slate-400 text-slate-900',
  'Gold': 'bg-gradient-to-r from-yellow-400 to-amber-500 text-amber-900',
  'Silver': 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800',
  'Bronze': 'bg-gradient-to-r from-orange-400 to-orange-600 text-white',
  'Minimum Coverage': 'bg-gray-200 text-gray-700',
  'Catastrophic': 'bg-red-100 text-red-700',
};

type SortOption = 'tier' | 'cost-low' | 'cost-high' | 'carrier' | 'name';
type ViewMode = 'grid' | 'employee';

interface EmployeePlanComparisonViewProps {
  employees: any[];
  plans: any[];
  calculateContribution: (premium: number, isDependent?: boolean, enrolleeCount?: number) => number;
  formatCurrency: (amount: number) => string;
  colors: { primary: string; accent: string; text: string; bg: string; border: string };
}

function EmployeePlanComparisonView({ 
  employees, 
  plans, 
  calculateContribution, 
  formatCurrency,
  colors 
}: EmployeePlanComparisonViewProps) {
  const [showAllBenefits, setShowAllBenefits] = useState(false);
  const [expandedEmployees, setExpandedEmployees] = useState<Set<number>>(new Set());

  if (employees.length === 0) {
    return (
      <Card className="py-12">
        <CardContent className="text-center">
          <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No employee data available</h3>
          <p className="text-muted-foreground">Employee cost breakdown is not available for this quote.</p>
        </CardContent>
      </Card>
    );
  }

  if (plans.length === 0) {
    return (
      <Card className="py-12">
        <CardContent className="text-center">
          <Filter className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No plans available</h3>
          <p className="text-muted-foreground">Please select at least one plan to view the employee breakdown.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Employee Cost Breakdown by Plan
          </CardTitle>
          <CardDescription>
            Compare how each plan affects employee costs. Each row shows an employee and their dependents.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-w-full">
            <Table className="min-w-max">
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-background z-10 w-[160px] min-w-[160px]">
                    <div className="flex items-center gap-1">
                      Employee
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[220px]">
                          <p>Click employees with dependents to expand and see per-member premium rates across plans.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                  {plans.map((plan, idx) => (
                    <TableHead key={`${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2">
                      <div className="space-y-1">
                        <div className="font-semibold text-xs leading-tight line-clamp-2" title={plan.name}>
                          {plan.name}
                        </div>
                        <Badge className={cn("text-xs", 
                          plan.metalTier === 'Platinum' ? 'bg-slate-400 text-white' :
                          plan.metalTier === 'Gold' ? 'bg-yellow-500 text-white' :
                          plan.metalTier === 'Silver' ? 'bg-gray-400 text-white' :
                          plan.metalTier === 'Bronze' ? 'bg-orange-500 text-white' :
                          'bg-gray-200 text-gray-700'
                        )}>
                          {plan.metalTier || plan.coverageType?.charAt(0).toUpperCase() + plan.coverageType?.slice(1) || 'Plan'}
                        </Badge>
                        <div className="text-xs text-muted-foreground truncate">{plan.carrier}</div>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
            <TableBody>
              <TableRow className="bg-blue-50/50 dark:bg-blue-950/20" data-testid="cost-breakdown-ded">
                <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                  Deductible
                </TableCell>
                {plans.map((plan, idx) => (
                  <TableCell key={`ded-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                    {plan.deductible || '—'}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="bg-blue-50/50 dark:bg-blue-950/20" data-testid="cost-breakdown-oop">
                <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                  Out-of-Pocket Max
                </TableCell>
                {plans.map((plan, idx) => (
                  <TableCell key={`oop-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                    {plan.outOfPocketMax || '—'}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="bg-blue-50/50 dark:bg-blue-950/20" data-testid="cost-breakdown-copay">
                <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                  Copay (PCP)
                </TableCell>
                {plans.map((plan, idx) => (
                  <TableCell key={`copay-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                    {plan.copay || '—'}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="bg-blue-50/50 dark:bg-blue-950/20" data-testid="cost-breakdown-er">
                <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                  Emergency Room
                </TableCell>
                {plans.map((plan, idx) => (
                  <TableCell key={`er-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                    {plan.emergencyRoom || '—'}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="bg-blue-50/50 dark:bg-blue-950/20">
                <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                  Hospital
                </TableCell>
                {plans.map((plan, idx) => (
                  <TableCell key={`hosp-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                    {plan.hospital || '—'}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="bg-blue-50/50 dark:bg-blue-950/20">
                <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                  Rx (Generic)
                </TableCell>
                {plans.map((plan, idx) => (
                  <TableCell key={`rx-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                    {plan.rxGeneric || '—'}
                  </TableCell>
                ))}
              </TableRow>
              {showAllBenefits && (
                <>
                  <TableRow className="bg-blue-50/50 dark:bg-blue-950/20">
                    <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                      Coinsurance
                    </TableCell>
                    {plans.map((plan, idx) => (
                      <TableCell key={`coins-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                        {plan.coinsurance || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-blue-50/50 dark:bg-blue-950/20">
                    <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                      Specialist
                    </TableCell>
                    {plans.map((plan, idx) => (
                      <TableCell key={`spec-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                        {plan.specialist || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-blue-50/50 dark:bg-blue-950/20">
                    <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                      Surgery
                    </TableCell>
                    {plans.map((plan, idx) => (
                      <TableCell key={`surg-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                        {plan.surgery || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-blue-50/50 dark:bg-blue-950/20">
                    <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                      Urgent Care
                    </TableCell>
                    {plans.map((plan, idx) => (
                      <TableCell key={`urg-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                        {plan.urgentCare || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-blue-50/50 dark:bg-blue-950/20">
                    <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                      Rx (Brand)
                    </TableCell>
                    {plans.map((plan, idx) => (
                      <TableCell key={`rxb-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                        {plan.rxBrand || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="bg-blue-50/50 dark:bg-blue-950/20">
                    <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                      Preventive Care
                    </TableCell>
                    {plans.map((plan, idx) => (
                      <TableCell key={`prev-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                        {plan.preventiveCare || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                  {plans.some((p) => p.lab) && (
                    <TableRow className="bg-blue-50/50 dark:bg-blue-950/20" data-testid="cost-breakdown-lab">
                      <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                        Lab (Blood Work)
                      </TableCell>
                      {plans.map((plan, idx) => (
                        <TableCell key={`lab-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                          {plan.lab || '—'}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                  {plans.some((p) => p.imagingBasic) && (
                    <TableRow className="bg-blue-50/50 dark:bg-blue-950/20" data-testid="cost-breakdown-imgb">
                      <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                        Imaging (Basic)
                      </TableCell>
                      {plans.map((plan, idx) => (
                        <TableCell key={`imgb-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                          {plan.imagingBasic || '—'}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                  {plans.some((p) => p.imagingAdvanced) && (
                    <TableRow className="bg-blue-50/50 dark:bg-blue-950/20" data-testid="cost-breakdown-imga">
                      <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-950/20 z-10 w-[160px] min-w-[160px] font-medium text-sm">
                        Imaging (Advanced)
                      </TableCell>
                      {plans.map((plan, idx) => (
                        <TableCell key={`imga-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2 text-sm">
                          {plan.imagingAdvanced || '—'}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </>
              )}
              <TableRow className="bg-blue-50/30 dark:bg-blue-950/10 border-b-2">
                <TableCell className="sticky left-0 bg-blue-50/30 dark:bg-blue-950/10 z-10 w-[160px] min-w-[160px]">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowAllBenefits(!showAllBenefits)}
                    className="text-xs p-0 h-auto hover:bg-transparent"
                  >
                    {showAllBenefits ? (
                      <><ChevronDown className="h-3 w-3 mr-1" /> Hide benefits</>
                    ) : (
                      <><ChevronRight className="h-3 w-3 mr-1" /> Show more benefits</>
                    )}
                  </Button>
                </TableCell>
                {plans.map((plan, idx) => (
                  <TableCell key={`expand-${plan.id}-${idx}`} className="w-[140px] min-w-[140px]" />
                ))}
              </TableRow>
              {employees.map((employee: any) => {
                // Find enrollment data for this employee from the first plan (for coverage tier & dependents)
                const firstPlanEnrollment = plans[0]?.enrollments?.find(
                  (e: any) => e.employeeId === employee.id || e.employee?.id === employee.id
                );
                const members = firstPlanEnrollment?.calculation?.members || [];
                const dependentMembers = members.filter((m: any) => m.memberType !== 'employee');

                // Derive dependents: prefer the explicit dependents array, fall back to members
                let dependentsList = firstPlanEnrollment?.dependents || [];
                if (dependentsList.length === 0 && dependentMembers.length > 0) {
                  dependentsList = dependentMembers.map((m: any) => {
                    const nameParts = (m.name || '').trim().split(/\s+/);
                    return {
                      firstName: nameParts[0] || m.memberType?.charAt(0).toUpperCase() + m.memberType?.slice(1) || 'Dependent',
                      lastName: nameParts.slice(1).join(' ') || '',
                      relationship: m.memberType || 'dependent',
                    };
                  });
                }

                const hasDependents = dependentMembers.length > 0 || dependentsList.length > 0;
                const isExpanded = expandedEmployees.has(employee.id);

                // Coverage tier: use calculation value, or derive from members
                let coverageTier = firstPlanEnrollment?.calculation?.coverageTier;
                if (coverageTier) {
                  coverageTier = coverageTier.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase());
                } else if (members.length > 0) {
                  const hasSpouse = members.some((m: any) => m.memberType === 'spouse');
                  const hasChild = members.some((m: any) => m.memberType === 'child');
                  if (members.length === 1) {
                    coverageTier = 'Employee Only';
                  } else if (hasSpouse && !hasChild && members.length === 2) {
                    coverageTier = 'Employee + Spouse';
                  } else if (!hasSpouse && hasChild && members.length === 2) {
                    coverageTier = 'Employee + Child';
                  } else {
                    coverageTier = 'Employee + Family';
                  }
                } else {
                  coverageTier = 'Employee Only';
                }

                const toggleExpand = () => {
                  setExpandedEmployees(prev => {
                    const next = new Set(prev);
                    if (next.has(employee.id)) {
                      next.delete(employee.id);
                    } else {
                      next.add(employee.id);
                    }
                    return next;
                  });
                };

                return (
                  <React.Fragment key={employee.id}>
                    {/* Main employee row */}
                    <TableRow
                      className={hasDependents ? "cursor-pointer hover:bg-muted/30" : ""}
                      onClick={hasDependents ? toggleExpand : undefined}
                    >
                      <TableCell className="sticky left-0 bg-background z-10 font-medium w-[160px] min-w-[160px]">
                        <div className="flex items-center gap-1">
                          {hasDependents && (
                            isExpanded
                              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          )}
                          <div className="space-y-0.5">
                            <div className="text-sm">{employee.firstName} {employee.lastName}</div>
                            <div className="text-xs text-muted-foreground">
                              {coverageTier}
                              {hasDependents && (
                                <span className="ml-1">• {dependentsList.length} dep{dependentsList.length !== 1 ? 's' : ''}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      {plans.map((plan, idx) => {
                        const enrollment = plan.enrollments?.find(
                          (e: any) => e.employeeId === employee.id || e.employee?.id === employee.id
                        );

                        let employeeCost = 0;
                        let employerCost = 0;
                        let totalPremium = 0;
                        let empPremium = 0;
                        let depPremium = 0;

                        if (enrollment?.calculation) {
                          const planMembers = enrollment.calculation.members || [];
                          planMembers.forEach((member: any) => {
                            const rate = parseFloat(member.monthlyRate || 0);
                            if (member.memberType === 'employee') {
                              empPremium += rate;
                            } else {
                              depPremium += rate;
                            }
                          });
                          totalPremium = enrollment.calculation.totalMonthlyPremium
                            || enrollment.calculation.totalPremium
                            || enrollment.calculation.totalMonthlyRate
                            || (empPremium + depPremium)
                            || 0;
                          if (empPremium === 0 && depPremium === 0) {
                            empPremium = totalPremium;
                          }
                        } else {
                          const avgPremiumPerEnrollee = plan.enrollmentCount > 0
                            ? plan.totalMonthlyPremium / plan.enrollmentCount
                            : plan.totalMonthlyPremium;
                          totalPremium = avgPremiumPerEnrollee;
                          empPremium = totalPremium;
                        }

                        const empEmployerCost = calculateContribution(empPremium);
                        const depEmployerCost = calculateContribution(depPremium, true);
                        employerCost = empEmployerCost + depEmployerCost;
                        employeeCost = totalPremium - employerCost;

                        return (
                          <TableCell key={`${plan.id}-${idx}-${employee.id}`} className="text-center w-[140px] min-w-[140px] px-2 align-top pt-3">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="space-y-0.5 cursor-help">
                                  <div className="font-semibold text-base">
                                    {formatCurrency(employeeCost)}
                                  </div>
                                  <div className="text-xs text-green-600 dark:text-green-400">
                                    +{formatCurrency(employerCost)} ER
                                  </div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[240px] text-left">
                                <div className="space-y-1 text-xs">
                                  <div className="font-semibold border-b pb-1 mb-1">Monthly Cost Breakdown</div>
                                  <div className="flex justify-between"><span>Total premium:</span><span className="font-medium">{formatCurrency(totalPremium)}</span></div>
                                  {depPremium > 0 && (
                                    <>
                                      <div className="flex justify-between text-muted-foreground"><span>Employee premium:</span><span>{formatCurrency(empPremium)}</span></div>
                                      <div className="flex justify-between text-muted-foreground"><span>Dependent premium:</span><span>{formatCurrency(depPremium)}</span></div>
                                    </>
                                  )}
                                  <div className="flex justify-between text-green-600"><span>Employer pays:</span><span>-{formatCurrency(employerCost)}</span></div>
                                  <div className="flex justify-between font-semibold border-t pt-1 mt-1"><span>Employee pays:</span><span>{formatCurrency(employeeCost)}</span></div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {/* Expanded dependent member rows */}
                    {hasDependents && isExpanded && members.map((member: any, mIdx: number) => {
                      let memberName = "";
                      let memberIcon: React.ReactNode;

                      if (member.memberType === "employee") {
                        memberName = `${employee.firstName} ${employee.lastName}`;
                        memberIcon = <User className="h-3 w-3 text-muted-foreground" />;
                      } else if (member.memberType === "spouse") {
                        const spouse = dependentsList.find((d: any) => d.relationship?.toLowerCase() === "spouse");
                        memberName = spouse && spouse.firstName ? `${spouse.firstName} ${spouse.lastName || ''}`.trim() : (member.name || "Spouse");
                        memberIcon = <Users className="h-3 w-3 text-muted-foreground" />;
                      } else {
                        const children = dependentsList.filter((d: any) => d.relationship?.toLowerCase()?.startsWith("child"));
                        const childIndex = members.slice(0, mIdx).filter((m: any) => m.memberType === "child").length;
                        const child = children[childIndex];
                        memberName = child && child.firstName ? `${child.firstName} ${child.lastName || ''}`.trim() : (member.name || `Child ${childIndex + 1}`);
                        memberIcon = <Baby className="h-3 w-3 text-muted-foreground" />;
                      }

                      return (
                        <TableRow key={`${employee.id}-member-${mIdx}`} className="bg-muted/20">
                          <TableCell className="sticky left-0 bg-muted/20 z-10 w-[160px] min-w-[160px] py-1.5">
                            <div className="flex items-center gap-1.5 ml-5 border-l-2 border-muted-foreground/20 pl-2">
                              {memberIcon}
                              <span className="text-xs">{memberName}</span>
                              {member.age !== null && member.age !== undefined && (
                                <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">{member.age}y</Badge>
                              )}
                            </div>
                          </TableCell>
                          {plans.map((plan, idx) => {
                            const enrollment = plan.enrollments?.find(
                              (e: any) => e.employeeId === employee.id || e.employee?.id === employee.id
                            );
                            const planMembers = enrollment?.calculation?.members || [];
                            const planMember = planMembers[mIdx];
                            const rate = planMember ? parseFloat(planMember.monthlyRate || 0) : 0;

                            const memberLabel = member.memberType === 'employee' ? 'Employee' : member.memberType === 'spouse' ? 'Spouse' : 'Child';

                            return (
                              <TableCell key={`${plan.id}-${idx}-${employee.id}-m${mIdx}`} className="text-center w-[140px] min-w-[140px] px-2 py-1.5">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-muted-foreground cursor-help">{formatCurrency(rate)}/mo</span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p className="text-xs">Base monthly premium for {memberLabel.toLowerCase()}{planMember?.age != null ? ` (age ${planMember.age})` : ''}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <TableRow className="bg-muted/50 font-semibold">
                <TableCell className="sticky left-0 bg-muted/50 z-10 w-[160px] min-w-[160px]">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1 cursor-help">
                        Total Monthly
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[220px]">
                      <p>Sum of all employee and dependent premiums for this plan, split by who pays what.</p>
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                {plans.map((plan, idx) => {
                  const totalPremium = plan.totalMonthlyPremium || 0;
                  const enrolleeCount = plan.enrollmentCount || plan.enrollments?.length || 1;
                  const empPrem = plan.employeeOnlyPremium || totalPremium;
                  const depPrem = plan.dependentPremium || 0;
                  const employerPays = calculateContribution(empPrem, false, enrolleeCount) + calculateContribution(depPrem, true, enrolleeCount);
                  const employeePays = totalPremium - employerPays;

                  return (
                    <TableCell key={`total-${plan.id}-${idx}`} className="text-center w-[140px] min-w-[140px] px-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="space-y-0.5 cursor-help">
                            <div className="text-base font-bold">{formatCurrency(totalPremium)}</div>
                            <div className="text-xs">
                              <span className="text-muted-foreground">EE: </span>
                              <span>{formatCurrency(employeePays)}</span>
                              <span className="mx-1">|</span>
                              <span className="text-green-600 dark:text-green-400">ER: {formatCurrency(employerPays)}</span>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[240px] text-left">
                          <div className="space-y-1 text-xs">
                            <div className="font-semibold border-b pb-1 mb-1">Plan Total — All Employees</div>
                            <div className="flex justify-between"><span>Total premium:</span><span className="font-medium">{formatCurrency(totalPremium)}</span></div>
                            {depPrem > 0 && (
                              <>
                                <div className="flex justify-between text-muted-foreground"><span>Employee premiums:</span><span>{formatCurrency(empPrem)}</span></div>
                                <div className="flex justify-between text-muted-foreground"><span>Dependent premiums:</span><span>{formatCurrency(depPrem)}</span></div>
                              </>
                            )}
                            <div className="flex justify-between"><span>EE = Employees pay:</span><span>{formatCurrency(employeePays)}</span></div>
                            <div className="flex justify-between text-green-600"><span>ER = Employer pays:</span><span>{formatCurrency(employerPays)}</span></div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PublicQuote() {
  const params = useParams<{ token: string }>();
  const token = params.token;
  const { user } = useAuth();

  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [filters, setFilters] = useState({
    carriers: [] as string[],
    metalTiers: [] as string[],
    coverageTypes: [] as string[],
    minCost: 0,
    maxCost: 10000,
  });
  const [contributionMode, setContributionMode] = useState<'percent' | 'flat'>('percent');
  const [employeeContributionPercent, setEmployeeContributionPercent] = useState(50);
  const [dependentContributionPercent, setDependentContributionPercent] = useState(0);
  const [employeeFlatContribution, setEmployeeFlatContribution] = useState(500);
  const [dependentFlatContribution, setDependentFlatContribution] = useState(0);
  const [basePlanId, setBasePlanId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('tier');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showFilters, setShowFilters] = useState(true);
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const [selectedPlanIds, setSelectedPlanIds] = useState<Set<string>>(new Set());
  const [selectedCarrier, setSelectedCarrier] = useState<string | null>(null);

  const { data: quoteData, isLoading, error } = useQuery({
    queryKey: ["/api/public/quote", token],
    queryFn: async () => {
      const response = await fetch(`/api/public/quote/${token}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to load quote");
      }
      return response.json();
    },
    enabled: !!token,
  });

  const settings: ProposalSettings = quoteData?.proposalSettings || {
    template: "modern",
    colorScheme: "blue",
    showLogo: true,
    showBenefitsDetail: true,
    showEmployeeBreakdown: true,
    coverLetter: "",
  };

  const colors = colorSchemeClasses[settings.colorScheme] || colorSchemeClasses.blue;
  const wizardState = quoteData?.wizardState || {};
  const selectedPlansFromWizard = wizardState.selectedPlans || {};

  // Seed the contribution calculator from the broker's employer-contribution
  // strategy (employee share / dependent share) when the quote carries one, so
  // the client sees the proposal as priced — not a hardcoded default.
  const brokerContribution = wizardState.contribution;
  useEffect(() => {
    if (!brokerContribution || typeof brokerContribution !== "object") return;
    const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : null);
    const employee = num(brokerContribution.employee ?? brokerContribution.employeeOnly);
    const dependent = num(brokerContribution.dependent ?? brokerContribution.employeeWithDependents);
    if (brokerContribution.type === "fixed") {
      setContributionMode("flat");
      if (employee != null) setEmployeeFlatContribution(employee);
      if (dependent != null) setDependentFlatContribution(dependent);
    } else {
      setContributionMode("percent");
      if (employee != null) setEmployeeContributionPercent(Math.min(100, Math.max(0, employee)));
      if (dependent != null) setDependentContributionPercent(Math.min(100, Math.max(0, dependent)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(brokerContribution ?? null)]);
  const premiumSummary = quoteData?.premiumSummary || wizardState?.premiumSummary || {};
  const planBreakdowns = premiumSummary?.planBreakdowns || [];
  
  const extractCarrierName = (plan: any): string => {
    if (!plan) return 'Unknown Carrier';
    if (typeof plan.carrier === 'string' && plan.carrier.trim()) return plan.carrier;
    if (plan.carrier && typeof plan.carrier === 'object' && plan.carrier.name) return plan.carrier.name;
    if (plan.carrierName && typeof plan.carrierName === 'string') return plan.carrierName;
    return 'Unknown Carrier';
  };

  // Shared by the renewal plan grid (allPlans, below) and "Your Current
  // Plan" — one mapping from a premium breakdown to the display shape both
  // use, so the two don't drift into reading benefit fields differently.
  const breakdownToDisplayPlan = (breakdown: any) => {
    const plan = breakdown.plan || {};
    const carrierName = extractCarrierName(plan);

    // Compute employee-only vs dependent premium from enrollment member data
    let employeeOnlyPremium = 0;
    let dependentPremium = 0;
    const enrollments = breakdown.enrollments || [];
    if (enrollments.length > 0) {
      enrollments.forEach((enrollment: any) => {
        const members = enrollment.calculation?.members || [];
        members.forEach((member: any) => {
          const rate = parseFloat(member.monthlyRate || 0);
          if (member.memberType === 'employee') {
            employeeOnlyPremium += rate;
          } else {
            dependentPremium += rate;
          }
        });
      });
    } else {
      // No enrollment data — treat all premium as employee-only
      employeeOnlyPremium = breakdown.totalMonthlyPremium || 0;
      dependentPremium = 0;
    }

    return {
      id: plan.id,
      name: plan.name || plan.planName || 'Unnamed Plan',
      carrier: carrierName,
      metalTier: plan.metalTier || plan.metal_tier || plan.tier || plan.planTier || null,
      coverageType: breakdown.coverageType || plan.coverageType || 'health',
      monthlyPremium: breakdown.totalMonthlyPremium || 0,
      totalMonthlyPremium: breakdown.totalMonthlyPremium || 0,
      employeeOnlyPremium,
      dependentPremium,
      employeePortion: breakdown.employeePortion || 0,
      employerPortion: breakdown.employerPortion || 0,
      enrollmentCount: breakdown.enrollmentCount || 0,
      enrollments,
      deductible: plan.deductible || plan.annualDeductible,
      outOfPocketMax: plan.outOfPocketMax || plan.maxOutOfPocket,
      copay: plan.copay || plan.primaryCareCopay,
      coinsurance: plan.coinsurance,
      emergencyRoom: plan.emergencyRoom || plan.erCopay || plan.emergencyRoomCopay,
      hospital: plan.hospital || plan.hospitalCopay || plan.inpatientHospital,
      surgery: plan.surgery || plan.surgeryCopay || plan.outpatientSurgery,
      specialist: plan.specialist || plan.specialistCopay,
      urgentCare: plan.urgentCare || plan.urgentCareCopay,
      rxGeneric: plan.rxGeneric || plan.genericDrugs || plan.rxTier1,
      rxBrand: plan.rxBrand || plan.brandDrugs || plan.rxTier2,
      preventiveCare: plan.preventiveCare || plan.preventiveCareCopay,
      lab: plan.lab,
      imagingBasic: plan.imagingBasic,
      imagingAdvanced: plan.imagingAdvanced,
    };
  };

  const currentPremiumSummary = quoteData?.currentPremiumSummary || wizardState?.currentPremiumSummary || {};
  const currentPlansDisplay = useMemo(
    () => (currentPremiumSummary?.planBreakdowns || []).map(breakdownToDisplayPlan),
    [currentPremiumSummary],
  );

  const allPlans = useMemo(() => {
    const plans: any[] = [];

    if (planBreakdowns.length > 0) {
      planBreakdowns.forEach((breakdown: any) => {
        plans.push(breakdownToDisplayPlan(breakdown));
      });
    } else {
      Object.entries(selectedPlansFromWizard).forEach(([coverageType, typePlans]: [string, any]) => {
        if (Array.isArray(typePlans)) {
          typePlans.forEach((plan: any) => {
            const carrierName = extractCarrierName(plan);
            const totalPrem = plan.monthlyPremium || plan.totalMonthlyPremium || 0;
            plans.push({
              id: plan.id,
              name: plan.name || plan.planName || 'Unnamed Plan',
              carrier: carrierName,
              metalTier: plan.metalTier || plan.metal_tier || plan.tier || plan.planTier || null,
              coverageType,
              monthlyPremium: totalPrem,
              totalMonthlyPremium: totalPrem,
              employeeOnlyPremium: totalPrem,
              dependentPremium: 0,
              enrollments: [],
              deductible: plan.deductible || plan.annualDeductible,
              outOfPocketMax: plan.outOfPocketMax || plan.maxOutOfPocket,
              copay: plan.copay || plan.primaryCareCopay,
              coinsurance: plan.coinsurance,
              emergencyRoom: plan.emergencyRoom || plan.erCopay || plan.emergencyRoomCopay,
              hospital: plan.hospital || plan.hospitalCopay || plan.inpatientHospital,
              surgery: plan.surgery || plan.surgeryCopay || plan.outpatientSurgery,
              specialist: plan.specialist || plan.specialistCopay,
              urgentCare: plan.urgentCare || plan.urgentCareCopay,
              rxGeneric: plan.rxGeneric || plan.genericDrugs || plan.rxTier1,
              rxBrand: plan.rxBrand || plan.brandDrugs || plan.rxTier2,
              preventiveCare: plan.preventiveCare || plan.preventiveCareCopay,
              lab: plan.lab,
              imagingBasic: plan.imagingBasic,
              imagingAdvanced: plan.imagingAdvanced,
            });
          });
        }
      });
    }
    return plans;
  }, [selectedPlansFromWizard, planBreakdowns]);

  const availableCarriers = useMemo(() => {
    const carriers = new Set<string>();
    allPlans.forEach(plan => {
      const carrier = plan.carrier || plan.carrierName || "Unknown";
      carriers.add(carrier);
    });
    return Array.from(carriers).sort();
  }, [allPlans]);

  const availableTiers = useMemo(() => {
    const tiers = new Set<string>();
    allPlans.forEach(plan => {
      if (plan.metalTier) tiers.add(plan.metalTier);
    });
    return Array.from(tiers).sort((a, b) => (metalTierOrder[a] || 99) - (metalTierOrder[b] || 99));
  }, [allPlans]);

  const availableCoverageTypes = useMemo(() => {
    const types = new Set<string>();
    allPlans.forEach(plan => {
      if (plan.coverageType && plan.coverageType !== 'base') types.add(plan.coverageType);
    });
    return Array.from(types);
  }, [allPlans]);

  // min/max = real premiums (display); sliderMin/sliderMax = whole-dollar
  // bounds containing every plan (filter internals only).
  const planCostRange = useMemo(() => computePlanCostRange(allPlans.map(p => {
    const premium = p.monthlyPremium || p.totalMonthlyPremium || 0;
    return typeof premium === 'string' ? parseFloat(premium) : premium;
  })), [allPlans]);

  useEffect(() => {
    if (!filtersInitialized && allPlans.length > 0) {
      setFilters(prev => ({
        ...prev,
        minCost: planCostRange.sliderMin,
        maxCost: planCostRange.sliderMax,
      }));
      setFiltersInitialized(true);
    }
  }, [planCostRange, filtersInitialized, allPlans.length]);

  const employees = useMemo(() => {
    const dbEmployees: any[] = quoteData?.employees || [];
    const employeeMap = new Map<number, any>();

    // Start with DB employees
    dbEmployees.forEach((emp: any) => {
      employeeMap.set(emp.id, emp);
    });

    // Merge in employees from planBreakdowns enrollment data (in case DB doesn't have all records)
    planBreakdowns.forEach((breakdown: any) => {
      const enrollments = breakdown.enrollments || [];
      enrollments.forEach((enrollment: any) => {
        const empId = enrollment.employeeId || enrollment.employee?.id;
        if (empId && !employeeMap.has(empId)) {
          // Synthesize employee entry from enrollment data
          const emp = enrollment.employee;
          if (emp) {
            employeeMap.set(empId, { id: empId, firstName: emp.firstName, lastName: emp.lastName });
          } else if (enrollment.employeeName) {
            // Parse "FirstName LastName" format
            const parts = enrollment.employeeName.trim().split(/\s+/);
            const firstName = parts[0] || '';
            const lastName = parts.slice(1).join(' ') || '';
            employeeMap.set(empId, { id: empId, firstName, lastName });
          }
        }
      });
    });

    return Array.from(employeeMap.values());
  }, [quoteData?.employees, planBreakdowns]);

  const filteredAndSortedPlans = useMemo(() => {
    let filtered = allPlans.filter(plan => {
      const carrier = plan.carrier || plan.carrierName || "Unknown";
      const premium = parseFloat(plan.monthlyPremium || plan.totalMonthlyPremium || 0);
      
      if (filters.carriers.length > 0 && !filters.carriers.includes(carrier)) return false;
      if (filters.metalTiers.length > 0 && !filters.metalTiers.includes(plan.metalTier)) return false;
      if (filters.coverageTypes.length > 0 && !filters.coverageTypes.includes(plan.coverageType)) return false;
      // Unpriced plans are outside the cost range; don't let the price filter hide them.
      if (premium > 0 && (premium < filters.minCost || premium > filters.maxCost)) return false;
      
      return true;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'tier':
          const tierA = metalTierOrder[a.metalTier] || 99;
          const tierB = metalTierOrder[b.metalTier] || 99;
          return tierA - tierB;
        case 'cost-low':
          return parseFloat(a.monthlyPremium || 0) - parseFloat(b.monthlyPremium || 0);
        case 'cost-high':
          return parseFloat(b.monthlyPremium || 0) - parseFloat(a.monthlyPremium || 0);
        case 'carrier':
          return (a.carrier || a.carrierName || '').localeCompare(b.carrier || b.carrierName || '');
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        default:
          return 0;
      }
    });

    return filtered;
  }, [allPlans, filters, sortBy]);

  const basePlan = useMemo(() => {
    if (!basePlanId) return null;
    return allPlans.find(p => String(p.id) === basePlanId);
  }, [allPlans, basePlanId]);

  const calculateContribution = (premium: number, isDependent: boolean = false, enrolleeCount: number = 1) => {
    if (contributionMode === 'percent') {
      const percent = isDependent ? dependentContributionPercent : employeeContributionPercent;
      return premium * (percent / 100);
    }
    // For flat amount, multiply by enrollee count when calculating totals
    const flatAmount = isDependent ? dependentFlatContribution : employeeFlatContribution;
    const totalFlatContribution = flatAmount * enrolleeCount;
    return Math.min(totalFlatContribution, premium);
  };

  const toggleFilter = (type: 'carriers' | 'metalTiers' | 'coverageTypes', value: string) => {
    setFilters(prev => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter(v => v !== value)
        : [...prev[type], value]
    }));
  };

  const clearFilters = () => {
    setFilters({
      carriers: [],
      metalTiers: [],
      coverageTypes: [],
      minCost: planCostRange.sliderMin,
      maxCost: planCostRange.sliderMax,
    });
  };

  const activeFilterCount = filters.carriers.length + filters.metalTiers.length + filters.coverageTypes.length;

  const handlePlanSelection = (planId: string, planCarrier: string, coverageType?: string) => {
    setSelectedPlanIds(prev => {
      const next = new Set(prev);
      if (next.has(planId)) {
        next.delete(planId);
        // If no plans left, clear the carrier restriction
        if (next.size === 0) {
          setSelectedCarrier(null);
        }
        return next;
      } else {
        // Health plans can be from any carrier - no restriction
        // For dental, vision, and other ancillary products, restrict to same carrier
        const isHealthPlan = coverageType?.toLowerCase() === 'health' || coverageType?.toLowerCase() === 'medical';
        
        if (isHealthPlan) {
          // No carrier restriction for health plans
          next.add(planId);
        } else {
          // For non-health plans, apply carrier restriction
          if (prev.size === 0) {
            setSelectedCarrier(planCarrier);
          }
          if (selectedCarrier === null || selectedCarrier === planCarrier) {
            next.add(planId);
            if (selectedCarrier === null) {
              setSelectedCarrier(planCarrier);
            }
          }
        }
        return next;
      }
    });
  };

  const clearPlanSelections = () => {
    setSelectedPlanIds(new Set());
    setSelectedCarrier(null);
  };

  const getSelectedPlansDetails = useMemo(() => {
    const selectedDetails: any[] = [];
    selectedPlanIds.forEach(planId => {
      const plan = allPlans.find(p => `${p.id || ''}-${p.name}` === planId || String(p.id) === planId);
      if (plan) {
        const premium = parseFloat(plan.monthlyPremium || plan.totalMonthlyPremium || 0);
        const enrolleeCount = plan.enrollmentCount || plan.enrollments?.length || 1;
        const empPremium = plan.employeeOnlyPremium || premium;
        const depPremium = plan.dependentPremium || 0;
        const empEmployerPays = calculateContribution(empPremium, false, enrolleeCount);
        const depEmployerPays = calculateContribution(depPremium, true, enrolleeCount);
        const employerPays = empEmployerPays + depEmployerPays;
        const employeePays = premium - employerPays;
        selectedDetails.push({
          ...plan,
          planId,
          premium,
          employerPays,
          employeePays,
        });
      }
    });
    return selectedDetails;
  }, [selectedPlanIds, allPlans, calculateContribution]);

  const hasSelectedPlans = selectedPlanIds.size > 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="w-full px-4 lg:px-8 space-y-6">
          <Skeleton className="h-48 w-full rounded-xl" />
          <div className="grid grid-cols-4 gap-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error || !quoteData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Quote Not Found</h2>
            <p className="text-muted-foreground">
              {error instanceof Error ? error.message : "This quote link may have expired or is invalid."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <header className={cn("relative overflow-hidden", `bg-gradient-to-r ${colors.bg}`)}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnYtNGgydjRoNHYyaC00djRoLTJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        <div className="relative w-full px-4 lg:px-8 py-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
              <span className="text-white font-semibold text-lg">administr</span>
              {quoteData.agencyBranding?.agencyLogo && (
                <>
                  <div className="w-px h-8 bg-white/30" />
                  <img 
                    src={quoteData.agencyBranding.agencyLogo} 
                    alt={quoteData.agencyBranding?.agencyName || "Agency"} 
                    className="h-8 w-auto max-w-[120px] object-contain"
                  />
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <Badge variant="secondary" className="mb-3 bg-white/20 text-white border-0">
                Benefits Proposal
              </Badge>
              <h1 className="text-4xl font-bold text-white mb-2">
                {quoteData.companyName}
              </h1>
              <p className="text-white/80 text-lg">
                Comprehensive benefits package prepared for your team
              </p>
              {quoteData.brokerInfo?.agentName && (
                <p className="text-white/70 text-sm mt-2">
                  Presented by {quoteData.brokerInfo.agentName}
                  {quoteData.brokerInfo.agentLicenseNumber && ` / License: ${quoteData.brokerInfo.agentLicenseNumber}`}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <Card className="bg-white/10 backdrop-blur border-white/20">
                <CardContent className="py-4 px-6">
                  <div className="text-white/70 text-sm">Effective Date</div>
                  <div className="text-2xl font-bold text-white">
                    {quoteData.effectiveDate
                      ? formatDateLong(quoteData.effectiveDate)
                      : "To Be Determined"}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 lg:px-8 py-6">
        {settings.coverLetter && (
          <Card className="mb-8 border-l-4" style={{ borderLeftColor: 'var(--primary)' }}>
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <FileText className={cn("h-6 w-6 mt-1 shrink-0", colors.text)} />
                <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {settings.coverLetter}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className={cn("border-2 transition-all hover:shadow-lg", colors.border)}>
            <CardContent className="py-5 flex items-center gap-4">
              <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center shadow-sm", colors.accent)}>
                <Building2 className={cn("h-6 w-6", colors.text)} />
              </div>
              <div>
                <div className="text-sm text-muted-foreground font-medium">Company</div>
                <div className="font-bold text-lg">{quoteData.companyName}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={cn("border-2 transition-all hover:shadow-lg", colors.border)}>
            <CardContent className="py-5 flex items-center gap-4">
              <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center shadow-sm", colors.accent)}>
                <Users className={cn("h-6 w-6", colors.text)} />
              </div>
              <div>
                <div className="text-sm text-muted-foreground font-medium">Employees</div>
                {/* Same merged list the cost breakdown renders — older snapshots of prospect quotes froze employees as []. */}
                <div className="font-bold text-lg">{employees.length}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={cn("border-2 transition-all hover:shadow-lg", colors.border)}>
            <CardContent className="py-5 flex items-center gap-4">
              <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center shadow-sm", colors.accent)}>
                <Shield className={cn("h-6 w-6", colors.text)} />
              </div>
              <div>
                <div className="text-sm text-muted-foreground font-medium">Plans Available</div>
                <div className="font-bold text-lg">{allPlans.length}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={cn("border-2 transition-all hover:shadow-lg", colors.border)}>
            <CardContent className="py-5 flex items-center gap-4">
              <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center shadow-sm", colors.accent)}>
                <DollarSign className={cn("h-6 w-6", colors.text)} />
              </div>
              <div>
                <div className="text-sm text-muted-foreground font-medium">Monthly Range</div>
                <div className="font-bold text-lg">
                  {planCostRange.min > 0 
                    ? formatMonthlyRange(planCostRange.min, planCostRange.max)
                    : formatCurrency(quoteData.totalMonthlyPremium || 0)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          <aside className={cn("w-full lg:w-64 shrink-0 space-y-4", !showFilters && "hidden lg:block")}>
            <Card className="sticky top-4">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <SlidersHorizontal className="h-5 w-5" />
                    Filters & Options
                  </CardTitle>
                  {activeFilterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Contribution Calculator
                  </Label>
                  
                  <Tabs value={contributionMode} onValueChange={(v) => setContributionMode(v as 'percent' | 'flat')}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="percent">Percentage</TabsTrigger>
                      <TabsTrigger value="flat">Flat Amount</TabsTrigger>
                    </TabsList>
                    <TabsContent value="percent" className="mt-3 space-y-4">
                      <div className="space-y-3 p-3 border rounded-lg">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Employee</span>
                          <span className="font-bold">{employeeContributionPercent}%</span>
                        </div>
                        <Slider
                          value={[employeeContributionPercent]}
                          onValueChange={([val]) => setEmployeeContributionPercent(val)}
                          min={0}
                          max={100}
                          step={5}
                        />
                      </div>
                      <div className="space-y-3 p-3 border rounded-lg">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Dependent</span>
                          <span className="font-bold">{dependentContributionPercent}%</span>
                        </div>
                        <Slider
                          value={[dependentContributionPercent]}
                          onValueChange={([val]) => setDependentContributionPercent(val)}
                          min={0}
                          max={100}
                          step={5}
                        />
                      </div>
                    </TabsContent>
                    <TabsContent value="flat" className="mt-3 space-y-4">
                      <div className="space-y-3 p-3 border rounded-lg">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Employee</span>
                          <span className="font-bold">{formatCurrency(employeeFlatContribution)}</span>
                        </div>
                        <Slider
                          value={[employeeFlatContribution]}
                          onValueChange={([val]) => setEmployeeFlatContribution(val)}
                          min={0}
                          max={2000}
                          step={50}
                        />
                      </div>
                      <div className="space-y-3 p-3 border rounded-lg">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Dependent</span>
                          <span className="font-bold">{formatCurrency(dependentFlatContribution)}</span>
                        </div>
                        <Slider
                          value={[dependentFlatContribution]}
                          onValueChange={([val]) => setDependentFlatContribution(val)}
                          min={0}
                          max={2000}
                          step={50}
                        />
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Coverage Type</Label>
                  <div className="space-y-2">
                    {availableCoverageTypes.map((type) => {
                      const Icon = getCoverageIcon(type);
                      return (
                        <label key={type} className="flex items-center gap-3 cursor-pointer group">
                          <Checkbox
                            checked={filters.coverageTypes.includes(type)}
                            onCheckedChange={() => toggleFilter('coverageTypes', type)}
                          />
                          <Icon className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                          <span className="text-sm capitalize group-hover:text-foreground transition-colors">
                            {type}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Metal Tier</Label>
                  <div className="space-y-2">
                    {availableTiers.map((tier) => (
                      <label key={tier} className="flex items-center gap-3 cursor-pointer group">
                        <Checkbox
                          checked={filters.metalTiers.includes(tier)}
                          onCheckedChange={() => toggleFilter('metalTiers', tier)}
                        />
                        <Badge className={cn("text-xs", metalTierColors[tier] || 'bg-gray-100')}>
                          {tier}
                        </Badge>
                      </label>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Carrier</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {availableCarriers.map((carrier) => (
                      <label key={carrier} className="flex items-center gap-3 cursor-pointer group">
                        <Checkbox
                          checked={filters.carriers.includes(carrier)}
                          onCheckedChange={() => toggleFilter('carriers', carrier)}
                        />
                        <span className="text-sm group-hover:text-foreground transition-colors truncate">
                          {carrier}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <Label className="text-sm font-semibold">Monthly Cost Range</Label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Minimum</span>
                      <span className="font-medium">{formatCurrency((filters.minCost || planCostRange.sliderMin) <= planCostRange.sliderMin ? planCostRange.min : filters.minCost)}</span>
                    </div>
                    <Slider
                      value={[filters.minCost || planCostRange.sliderMin]}
                      onValueChange={([val]) => setFilters(prev => ({ ...prev, minCost: Math.min(val, prev.maxCost || planCostRange.sliderMax) }))}
                      min={planCostRange.sliderMin}
                      max={planCostRange.sliderMax}
                      step={50}
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Maximum</span>
                      <span className="font-medium">{formatCurrency((filters.maxCost || planCostRange.sliderMax) >= planCostRange.sliderMax ? planCostRange.max : filters.maxCost)}</span>
                    </div>
                    <Slider
                      value={[filters.maxCost || planCostRange.sliderMax]}
                      onValueChange={([val]) => setFilters(prev => ({ ...prev, maxCost: Math.max(val, prev.minCost || planCostRange.sliderMin) }))}
                      min={planCostRange.sliderMin}
                      max={planCostRange.sliderMax}
                      step={50}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>

          <div className="flex-1 space-y-4">
            {currentPlansDisplay.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-900 dark:bg-amber-950/20 space-y-3">
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  Your Current Plan{currentPlansDisplay.length > 1 ? "s" : ""}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentPlansDisplay.map((plan: any, index: number) => {
                    const Icon = getCoverageIcon(plan.coverageType);
                    return (
                      <Card key={`current-${plan.id || index}`} className="border-amber-200 dark:border-amber-900">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200 dark:bg-amber-900 dark:text-amber-200 text-xs shrink-0">
                                  CURRENT
                                </Badge>
                                <Badge variant="outline" className="text-xs capitalize shrink-0">
                                  <Icon className="h-3 w-3 mr-1" />
                                  {plan.coverageType}
                                </Badge>
                              </div>
                              <CardTitle className="text-base leading-tight">{plan.name}</CardTitle>
                              <CardDescription className="mt-1">{plan.carrier}</CardDescription>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-xl font-bold">
                                {plan.totalMonthlyPremium > 0 ? formatCurrency(plan.totalMonthlyPremium) : "—"}
                              </div>
                              <div className="text-xs text-muted-foreground">/month</div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="p-2 bg-muted/50 rounded text-center">
                              <div className="text-muted-foreground">Deductible</div>
                              <div className="font-semibold">{plan.deductible || "—"}</div>
                            </div>
                            <div className="p-2 bg-muted/50 rounded text-center">
                              <div className="text-muted-foreground">OOP Max</div>
                              <div className="font-semibold">{plan.outOfPocketMax || "—"}</div>
                            </div>
                            <div className="p-2 bg-muted/50 rounded text-center">
                              <div className="text-muted-foreground">Office Visit</div>
                              <div className="font-semibold">{plan.copay || "—"}</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  Compare against the renewal options below.
                </p>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-4 bg-background rounded-lg border p-4">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="lg:hidden"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-2">{activeFilterCount}</Badge>
                  )}
                </Button>
                <span className="text-sm text-muted-foreground">
                  Showing <strong>{filteredAndSortedPlans.length}</strong> of {allPlans.length} plans
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                  <SelectTrigger className="w-[180px]">
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tier">Metal Tier</SelectItem>
                    <SelectItem value="cost-low">Cost: Low to High</SelectItem>
                    <SelectItem value="cost-high">Cost: High to Low</SelectItem>
                    <SelectItem value="carrier">Carrier Name</SelectItem>
                    <SelectItem value="name">Plan Name</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex border rounded-md">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </Button>
<Button
                    variant={viewMode === 'employee' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('employee')}
                    title="Employee Breakdown"
                  >
                    <UserCheck className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {viewMode === 'employee' ? (
              <EmployeePlanComparisonView 
                employees={employees}
                plans={filteredAndSortedPlans}
                calculateContribution={calculateContribution}
                formatCurrency={formatCurrency}
                colors={colors}
              />
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredAndSortedPlans.map((plan, index) => {
                const Icon = getCoverageIcon(plan.coverageType);
                const premium = parseFloat(plan.monthlyPremium || plan.totalMonthlyPremium || 0);
                const enrolleeCount = plan.enrollmentCount || plan.enrollments?.length || 1;
                const empPremium = plan.employeeOnlyPremium || premium;
                const depPremium = plan.dependentPremium || 0;
                const empEmployerPays = calculateContribution(empPremium, false, enrolleeCount);
                const empEmployeePays = empPremium - empEmployerPays;
                const depEmployerPays = calculateContribution(depPremium, true, enrolleeCount);
                const depEmployeePays = depPremium - depEmployerPays;
                const totalEmployerPays = empEmployerPays + depEmployerPays;
                const totalEmployeePays = empEmployeePays + depEmployeePays;
                const planId = `${plan.id || index}-${plan.name}`;
                const isExpanded = expandedPlans.has(planId);
                const planCarrier = plan.carrier || plan.carrierName || 'Unknown Carrier';
                const isSelected = selectedPlanIds.has(planId);
                // Health plans can be from any carrier - no restriction for medical/health
                const isHealthPlan = plan.coverageType?.toLowerCase() === 'health' || plan.coverageType?.toLowerCase() === 'medical';
                const isDifferentCarrier = !isHealthPlan && selectedCarrier !== null && selectedCarrier !== planCarrier;

                return (
                  <Card 
                    key={planId} 
                    className={cn(
                      "overflow-hidden transition-all hover:shadow-lg border-2 relative",
                      isSelected ? "border-green-500 ring-2 ring-green-200 dark:ring-green-800" : colors.border,
                      isDifferentCarrier && "opacity-50"
                    )}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 z-10">
                        <Badge className="bg-green-500 text-white">
                          <Check className="h-3 w-3 mr-1" /> Selected
                        </Badge>
                      </div>
                    )}
                    <div className={cn("h-1.5", metalTierColors[plan.metalTier]?.split(' ')[0] || 'bg-gray-300')} />
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={cn("text-xs shrink-0", plan.metalTier ? (metalTierColors[plan.metalTier] || 'bg-gray-100 text-gray-700') : 'bg-gray-100 text-gray-700')}>
                              {plan.metalTier || plan.coverageType?.charAt(0).toUpperCase() + plan.coverageType?.slice(1) || 'Plan'}
                            </Badge>
                            <Badge variant="outline" className="text-xs capitalize shrink-0">
                              <Icon className="h-3 w-3 mr-1" />
                              {plan.coverageType}
                            </Badge>
                          </div>
                          <CardTitle className="text-lg leading-tight">
                            {plan.name || 'Unnamed Plan'}
                          </CardTitle>
                          <CardDescription className="mt-1">
                            {plan.carrier || plan.carrierName || 'Unknown Carrier'}
                          </CardDescription>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-2xl font-bold">
                            {formatCurrency(premium)}
                          </div>
                          <div className="text-xs text-muted-foreground">/month</div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-4">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div />
                          <div className="text-center font-semibold text-muted-foreground">Employer Pays</div>
                          <div className="text-center font-semibold text-muted-foreground">Employee Pays</div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 items-center mt-2">
                          <div className="text-xs font-medium">Employee</div>
                          <div className={cn("text-center font-bold text-sm", colors.text)}>
                            {formatCurrency(empEmployerPays)}
                          </div>
                          <div className="text-center font-bold text-sm">
                            {formatCurrency(empEmployeePays)}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 items-center mt-1">
                          <div className="text-xs font-medium">Dependents</div>
                          <div className={cn("text-center font-bold text-sm", colors.text)}>
                            {formatCurrency(depEmployerPays)}
                          </div>
                          <div className="text-center font-bold text-sm">
                            {formatCurrency(depEmployeePays)}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 items-center mt-2 pt-2 border-t">
                          <div className="text-xs font-semibold">Total</div>
                          <div className={cn("text-center font-bold text-base", colors.text)}>
                            {formatCurrency(totalEmployerPays)}
                          </div>
                          <div className="text-center font-bold text-base">
                            {formatCurrency(totalEmployeePays)}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="p-2 bg-muted/50 rounded text-center">
                          <div className="text-muted-foreground">Deductible</div>
                          <div className="font-semibold">{plan.deductible || '—'}</div>
                        </div>
                        <div className="p-2 bg-muted/50 rounded text-center">
                          <div className="text-muted-foreground">OOP Max</div>
                          <div className="font-semibold">{plan.outOfPocketMax || '—'}</div>
                        </div>
                        <div className="p-2 bg-muted/50 rounded text-center">
                          <div className="text-muted-foreground">Copay</div>
                          <div className="font-semibold">{plan.copay || '—'}</div>
                        </div>
                      </div>

                      {settings.showBenefitsDetail && (
                        <Collapsible 
                          open={isExpanded}
                          onOpenChange={(open) => {
                            setExpandedPlans(prev => {
                              const next = new Set(prev);
                              if (open) next.add(planId);
                              else next.delete(planId);
                              return next;
                            });
                          }}
                        >
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" className="w-full justify-between" size="sm">
                              <span className="flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                View All Benefits
                              </span>
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="pt-3">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              {plan.emergencyRoom && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">ER</span>
                                  <span className="font-medium">{plan.emergencyRoom}</span>
                                </div>
                              )}
                              {plan.hospital && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Hospital</span>
                                  <span className="font-medium">{plan.hospital}</span>
                                </div>
                              )}
                              {plan.specialist && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Specialist</span>
                                  <span className="font-medium">{plan.specialist}</span>
                                </div>
                              )}
                              {plan.surgery && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Surgery</span>
                                  <span className="font-medium">{plan.surgery}</span>
                                </div>
                              )}
                              {plan.urgentCare && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Urgent Care</span>
                                  <span className="font-medium">{plan.urgentCare}</span>
                                </div>
                              )}
                              {plan.lab && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Lab</span>
                                  <span className="font-medium">{plan.lab}</span>
                                </div>
                              )}
                              {plan.imagingBasic && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Imaging (basic)</span>
                                  <span className="font-medium">{plan.imagingBasic}</span>
                                </div>
                              )}
                              {plan.imagingAdvanced && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Imaging (advanced)</span>
                                  <span className="font-medium">{plan.imagingAdvanced}</span>
                                </div>
                              )}
                              {plan.rxGeneric && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Rx Generic</span>
                                  <span className="font-medium">{plan.rxGeneric}</span>
                                </div>
                              )}
                              {plan.rxBrand && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Rx Brand</span>
                                  <span className="font-medium">{plan.rxBrand}</span>
                                </div>
                              )}
                              {plan.coinsurance && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Coinsurance</span>
                                  <span className="font-medium">{plan.coinsurance}</span>
                                </div>
                              )}
                              {plan.preventiveCare && (
                                <div className="flex justify-between p-2 bg-muted rounded">
                                  <span className="text-muted-foreground">Preventive</span>
                                  <span className="font-medium">{plan.preventiveCare}</span>
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}

                      <Button
                        variant={isSelected ? "default" : "outline"}
                        className={cn(
                          "w-full mt-2",
                          isSelected && "bg-green-600 hover:bg-green-700",
                          isDifferentCarrier && "opacity-50 cursor-not-allowed"
                        )}
                        onClick={() => handlePlanSelection(planId, planCarrier, plan.coverageType)}
                        disabled={isDifferentCarrier}
                      >
                        {isSelected ? (
                          <><Check className="h-4 w-4 mr-2" /> Selected</>
                        ) : isDifferentCarrier ? (
                          <>Only {selectedCarrier} plans can be selected</>
                        ) : (
                          <>Select this plan</>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            )}

            {filteredAndSortedPlans.length === 0 && viewMode !== 'employee' && (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Filter className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">No plans match your filters</h3>
                  <p className="text-muted-foreground mb-4">Try adjusting your filter criteria</p>
                  <Button variant="outline" onClick={clearFilters}>
                    Clear All Filters
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <div className="mt-12 space-y-6">
          {hasSelectedPlans && (
            <Card className="border-2 border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-green-800 dark:text-green-200">
                      <Check className="h-5 w-5" />
                      Selected Plans Summary
                    </CardTitle>
                    <CardDescription>
                      {selectedPlanIds.size} plan{selectedPlanIds.size !== 1 ? 's' : ''} selected from <strong>{selectedCarrier}</strong>
                    </CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearPlanSelections}>
                    <X className="h-4 w-4 mr-1" /> Clear All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {getSelectedPlansDetails.map((plan, idx) => {
                    const Icon = getCoverageIcon(plan.coverageType);
                    return (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white dark:bg-background rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className={cn("p-2 rounded-lg", colors.bg)}>
                            <Icon className={cn("h-5 w-5", colors.text)} />
                          </div>
                          <div>
                            <div className="font-medium">{plan.name}</div>
                            <div className="text-sm text-muted-foreground capitalize">
                              {plan.coverageType}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">{formatCurrency(plan.premium)}/mo</div>
                          <div className="text-xs text-muted-foreground">
                            Employer: {formatCurrency(plan.employerPays)} | Employee: {formatCurrency(plan.employeePays)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {!hasSelectedPlans && (
            <Card className="border-2 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
              <CardContent className="py-8 text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-600" />
                <h3 className="text-lg font-semibold mb-2">No Plans Selected</h3>
                <p className="text-muted-foreground">
                  Please select the plans you wish to enroll in. You may select multiple plans, but all must be from the same carrier.
                </p>
              </CardContent>
            </Card>
          )}

          <QuoteAcceptanceForm
            token={token || ""}
            quoteId={quoteData.id}
            companyName={quoteData.companyName}
            isVersioned={false}
            acceptance={quoteData.acceptance}
            selectedPlans={getSelectedPlansDetails}
            superseded={!!(quoteData as any).superseded}
            // The split the client chose on the calculator. It drove the totals
            // they saw, so it belongs in the record of what they accepted.
            contributionStrategy={{
              mode: contributionMode,
              // Despite the variable names, these are the EMPLOYER's share:
              // calculateContribution() returns what the employer pays. Stored
              // under unambiguous keys so nobody has to re-derive that later.
              employerPercentEmployeeTier: employeeContributionPercent,
              employerPercentDependents: dependentContributionPercent,
              employerFlatEmployeeTier: employeeFlatContribution,
              employerFlatDependents: dependentFlatContribution,
            }}
            disabled={!hasSelectedPlans}
            // A logged-in employer of this quote's client gets a verified,
            // finalizing acceptance; everyone else submits for broker review.
            verified={
              (user as any)?.role === "employer" &&
              !!(user as any)?.clientId &&
              (user as any).clientId === quoteData.clientId
            }
          />
        </div>

        {quoteData.brokerInfo && (
          <Card className={cn("mt-8 border-2", colors.border)}>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <UserCheck className={cn("h-5 w-5", colors.text)} />
                Your Benefits Advisor
              </CardTitle>
              <CardDescription>
                Contact your dedicated broker for questions about this proposal
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {quoteData.brokerInfo.agencyName && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Agency Information
                    </h4>
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">{quoteData.brokerInfo.agencyName}</span>
                    </div>
                    {quoteData.brokerInfo.agencyLicenseNumber && (
                      <div className="flex items-center gap-3">
                        <BadgeCheck className="h-5 w-5 text-muted-foreground" />
                        <span className="text-sm">License: {quoteData.brokerInfo.agencyLicenseNumber}</span>
                      </div>
                    )}
                  </div>
                )}
                {quoteData.brokerInfo.agentName && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Producing Agent
                    </h4>
                    <div className="flex items-center gap-3">
                      <UserCheck className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">{quoteData.brokerInfo.agentName}</span>
                    </div>
                    {quoteData.brokerInfo.agentLicenseNumber && (
                      <div className="flex items-center gap-3">
                        <BadgeCheck className="h-5 w-5 text-muted-foreground" />
                        <span className="text-sm">License: {quoteData.brokerInfo.agentLicenseNumber}</span>
                      </div>
                    )}
                    {quoteData.brokerInfo.agentEmail && (
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-muted-foreground" />
                        <a href={`mailto:${quoteData.brokerInfo.agentEmail}`} className={cn("text-sm hover:underline", colors.text)}>
                          {quoteData.brokerInfo.agentEmail}
                        </a>
                      </div>
                    )}
                    {quoteData.brokerInfo.agentPhone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-5 w-5 text-muted-foreground" />
                        <a href={`tel:${quoteData.brokerInfo.agentPhone}`} className={cn("text-sm hover:underline", colors.text)}>
                          {quoteData.brokerInfo.agentPhone}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <footer className="text-center text-sm text-muted-foreground py-12 border-t mt-12 max-w-4xl mx-auto">
          <p className="mb-4 leading-relaxed">
            This proposal is an estimate only and does not constitute a contract of insurance. Rates are subject to final carrier approval and may vary based on the final effective date, verified census data, and the employer's eligibility. Proposed premiums are based on current rates which are subject to change by the carrier on a quarterly/annual basis. Final rates and coverage are not bound until the carrier provides written confirmation of approval and a formal policy is issued.
          </p>
          <p>For questions, please contact your benefits advisor.</p>
        </footer>
      </main>
    </div>
  );
}
